package site.shadadman.pages.worklog.SportsGameX

import androidx.compose.runtime.*
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.data.*
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.core.layout.NoLayout
import com.varabyte.kobweb.core.init.InitRoute
import com.varabyte.kobweb.core.init.InitRouteContext
import com.varabyte.kobwebx.frontmatter.*
import com.varabyte.kobwebx.markdown.*

@InitRoute
fun initJul302025SportsGamexTradingViewChartsPage(ctx: InitRouteContext) {
    ctx.data.add(MarkdownContext("worklog/SportsGameX/jul302025SportsGamexTradingViewCharts.md", FrontMatterElement.EmptyMap))
}

@Page("/worklog/SportsGameX/jul302025-sports-gamex-trading-view-charts")
@Composable
fun Jul302025SportsGamexTradingViewChartsPage() {
    org.jetbrains.compose.web.dom.H2(attrs = { id("jul-30-2025-sportsgamex-tradingview-charts") }) {
        org.jetbrains.compose.web.dom.Text("jul-30-2025-SportsGamex-TradingView-Charts")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("One thing that puts Kotlin Multiplatform ahead of competitors like Flutter or React Native is its ability to implement platform specific features. We wanted to use ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("TradingView")
        }
        org.jetbrains.compose.web.dom.Text(" charts on the client side. If we were using something like early stages Flutter, we would have had to wait for a library or package to support those charts. But thanks to KMP’s flexibility, we were able to use the official iOS and Android documentation from ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("TradingView")
        }
        org.jetbrains.compose.web.dom.Text(" and build our feature, just like implementing it separately for each mobile platform.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Of course, this was a bit time consuming because it was essentially like implementing everything separately for each mobile platform. But I believe the time was absolutely worth it, because the implementation is fully native and any future changes will only require minor refactoring.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The charts that we needed were the famous ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("CandlerChart")
        }
        org.jetbrains.compose.web.dom.Text(", ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("LineChart")
        }
        org.jetbrains.compose.web.dom.Text(", ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("MiniGraph")
        }
        org.jetbrains.compose.web.dom.Text(" and ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("MiniLineChart")
        }
        org.jetbrains.compose.web.dom.Text(". Each chart had its own data model and a ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("Composable")
        }
        org.jetbrains.compose.web.dom.Text(" Api. But we managed all of them in a unified Api as an ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("expect")
        }
        org.jetbrains.compose.web.dom.Text(" mechanism and we called it ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("TradinView")
        }
        org.jetbrains.compose.web.dom.Text(":")
    }
    org.jetbrains.compose.web.dom.Pre { org.jetbrains.compose.web.dom.Code { org.jetbrains.compose.web.dom.Text("""@Composable
expect fun TradingView(
    modifier: Modifier = Modifier,
    chartPoints: List<ChartPoint>,
    chartType: ChartType,
    timePeriod: TimePeriod,
    chartBackgroundColor: Color,
    axisLabelTextColor: Color,
    lineColor: Color,
    positiveColor: Color,
    negativeColor: Color,
    crossHairLabelBg: Color,
    dragLineColor: Color,
    gridLinesColor: Color,
    dateFormat: String,
    locale: String,
    localCurrencySymbol: String,
    isDarkMode: Boolean,
    onGraphState: (GraphState) -> Unit,
    onGraphDrag: (ChartPoint?) -> Unit
)

enum class GraphState {
    READY, PREPARING, ERROR
}
""") } }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Of course, each property explains itself by its name, but as a reminder to myself, I’d like to include the Compose implementation of one of the charts here. This is the ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("MiniGraph")
        }
        org.jetbrains.compose.web.dom.Text(":")
    }
    org.jetbrains.compose.web.dom.Pre { org.jetbrains.compose.web.dom.Code { org.jetbrains.compose.web.dom.Text("""@Composable
fun MiniGraph(
    modifier: Modifier = Modifier,
    chartPoints: List<Float>,
    lineColor: Color
) {
    val zipList: List<Pair<Float, Float>> = chartPoints.zipWithNext()
    Row(modifier = modifier) {
        val max = chartPoints.max()
        val min = chartPoints.min()

        for (pair in zipList) {

            val fromValuePercentage = getValuePercentageForRange(pair.first, max, min)
            val toValuePercentage = getValuePercentageForRange(pair.second, max, min)

            Canvas(
                modifier = Modifier
                    .fillMaxHeight()
                    .weight(1f),
                onDraw = {
                    val fromPoint = Offset(
                        x = 0f,
                        y = size.height.times(1 - fromValuePercentage)
                    ) // <-- Use times so it works for any available space
                    val toPoint =
                        Offset(
                            x = size.width,
                            y = size.height.times(1 - toValuePercentage)
                        ) // <-- Also here!

                    drawLine(
                        color = lineColor,
                        start = fromPoint,
                        end = toPoint,
                        strokeWidth = 3f
                    )
                })
        }
    }
}
""") } }
}
